import { useEffect, useMemo, useState } from 'react';
import API from '../api';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const pct = v => `${Number(v || 0).toFixed(2)}%`;
const inputStyle = { background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f' };

const emptyForm = {
  fecha_entrada: new Date().toISOString().slice(0, 16),
  fecha_salida: '',
  plataforma: 'Exness',
  cuenta: '',
  instrumento: '',
  mercado: 'Acciones',
  direccion: 'LARGO',
  cantidad: '',
  capital: '',
  saldo_final: '',
  resultado: '',
  estado: 'CERRADA',
  estrategia: '',
  bitacora: '',
};

function minutesToText(minutes) {
  const n = Math.round(Number(minutes || 0));
  if (!n) return '—';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function toInputDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 16);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function TradingInversiones({ embedded = false, onCambio }) {
  const [resumen, setResumen] = useState(null);
  const [operaciones, setOperaciones] = useState([]);
  const [listas, setListas] = useState({ plataformas: [], cuentas: [], instrumentos: [] });
  const [filtros, setFiltros] = useState({ periodo: 'mes', plataforma: '', cuenta: '', estado: '', instrumento: '', desde: '', hasta: '' });
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const params = useMemo(() => {
    const p = {};
    Object.entries(filtros).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filtros]);

  async function cargar() {
    const [res, ops, lis] = await Promise.all([
      API.get('/trading/resumen', { params }),
      API.get('/trading/operaciones', { params }),
      API.get('/trading/listas'),
    ]);
    setResumen(res.data);
    setOperaciones(ops.data);
    setListas(lis.data);
  }

  useEffect(() => { cargar().catch(e => setMsg(e.response?.data?.error || e.message)); }, [params]);

  const resultado = parseFloat(form.resultado) || 0;
  const saldoFinalForm = parseFloat(form.saldo_final);
  const capital = Number.isFinite(saldoFinalForm) ? Math.max(0, saldoFinalForm - resultado) : (parseFloat(form.capital) || 0);

  function limpiar() {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  }

  function editar(op) {
    setEditId(op.id);
    setForm({
      fecha_entrada: toInputDate(op.fecha_entrada),
      fecha_salida: toInputDate(op.fecha_salida),
      plataforma: op.plataforma || 'Exness',
      cuenta: op.cuenta || '',
      instrumento: op.instrumento || '',
      mercado: op.mercado || 'Acciones',
      direccion: op.direccion === 'LONG' || op.direccion === 'COMPRA' ? 'LARGO' : op.direccion === 'SHORT' || op.direccion === 'VENTA' ? 'CORTO' : (op.direccion || 'LARGO'),
      cantidad: op.cantidad || '',
      capital: op.capital || '',
      saldo_final: op.capital != null || op.resultado != null ? (Number(op.capital || 0) + Number(op.resultado || 0)).toFixed(2) : '',
      resultado: op.resultado || '',
      estado: op.estado || 'CERRADA',
      estrategia: op.estrategia || '',
      bitacora: op.bitacora || '',
    });
    setShowForm(true);
  }

  async function guardar(e) {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    try {
      const saldoFinal = parseFloat(form.saldo_final);
      const capitalCalculado = Number.isFinite(saldoFinal) ? saldoFinal - resultado : capital;
      const payload = { ...form, capital: capitalCalculado > 0 ? capitalCalculado.toFixed(2) : '0', fecha_salida: form.fecha_salida || null };
      if (editId) await API.put(`/trading/operaciones/${editId}`, payload);
      else await API.post('/trading/operaciones', payload);
      limpiar();
      await cargar();
      if (onCambio) await onCambio();
      setMsg(editId ? 'Operación actualizada' : 'Operación guardada');
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta operación?')) return;
    await API.delete(`/trading/operaciones/${id}`);
    await cargar();
    if (onCambio) await onCambio();
  }

  const positivo = Number(resumen?.resultado || 0) >= 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          {!embedded && <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#08293f' }}>Trading e Inversiones</h1>}
          <div style={{ color: '#5a7a8a', fontSize: 12, marginTop: 4 }}>Control de operaciones, rendimiento y bitácora por plataforma.</div>
        </div>
        <button className="btn-primary" onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}>+ Operación</button>
      </div>

      {msg && <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #e8eef2', color: msg.startsWith('Error') ? '#dc2626' : '#059669', background: '#fff', fontWeight: 700, fontSize: 12 }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, marginBottom: 12 }}>
        {[
          { label: 'Resultado neto', value: fmt(resumen?.resultado), color: positivo ? '#059669' : '#dc2626', dark: true },
          // Embebido en Inversiones la tarjeta de cuenta ya muestra el % sobre el
          // monto inicial; aclaramos que este otro va sobre el capital operado.
          { label: embedded ? 'Rend. s/ capital operado' : 'Rendimiento', value: pct(resumen?.rendimiento_pct), color: positivo ? '#059669' : '#dc2626' },
          { label: 'Operaciones', value: resumen?.operaciones || 0, color: '#0284c7' },
          { label: 'Win rate', value: pct(resumen?.winrate), color: '#6366f1' },
          { label: 'Tiempo prom.', value: minutesToText(resumen?.minutos_promedio), color: '#b45309' },
        ].map(k => (
          <div key={k.label} style={{ background: k.dark ? '#08293f' : '#fff', border: k.dark ? '1px solid rgba(0,184,227,0.25)' : '1px solid #e8eef2', borderRadius: 2, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: k.dark ? 'rgba(0,184,227,0.65)' : '#5a7a8a', marginBottom: 5 }}>{k.label}</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: k.dark ? '#00b8e3' : k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        <select className="input" value={filtros.periodo} onChange={e => setFiltros(f => ({ ...f, periodo: e.target.value, desde: '', hasta: '' }))} style={inputStyle}>
          <option value="semana">Esta semana</option>
          <option value="mes">Este mes</option>
          <option value="anio">Este año</option>
          <option value="todo">Todo</option>
        </select>
        <select className="input" value={filtros.plataforma} onChange={e => setFiltros(f => ({ ...f, plataforma: e.target.value }))} style={inputStyle}>
          <option value="">Todas las plataformas</option>
          {['Exness', ...listas.plataformas.filter(p => p !== 'Exness')].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="input" value={filtros.cuenta} onChange={e => setFiltros(f => ({ ...f, cuenta: e.target.value }))} style={inputStyle}>
          <option value="">Todas las cuentas</option>
          {listas.cuentas.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input" value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))} style={inputStyle}>
          <option value="">Todos los estados</option>
          <option value="ABIERTA">Abiertas</option>
          <option value="CERRADA">Cerradas</option>
        </select>
        <input className="input" value={filtros.instrumento} onChange={e => setFiltros(f => ({ ...f, instrumento: e.target.value }))} placeholder="Buscar instrumento..." style={inputStyle} />
        <input className="input" type="date" value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde: e.target.value, periodo: 'todo' }))} style={inputStyle} />
        <input className="input" type="date" value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value, periodo: 'todo' }))} style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)', gap: 12 }} className="trading-layout">
        <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', background: '#f9fafb', borderBottom: '1px solid #e8eef2', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Operaciones</div>
              <div style={{ fontSize: 12, color: '#8aa0ad', marginTop: 2 }}>{operaciones.length} registros visibles</div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" style={{ margin: 0, minWidth: 980 }}>
              <thead>
                <tr>
                  <th>Entrada</th><th>Plataforma</th><th>Instrumento</th><th>Dir.</th><th style={{ textAlign: 'right' }}>Cantidad</th><th style={{ textAlign: 'right' }}>Base %</th><th style={{ textAlign: 'right' }}>Resultado</th><th style={{ textAlign: 'right' }}>%</th><th>Estado</th><th></th>
                </tr>
              </thead>
              <tbody>
                {operaciones.map(op => (
                  <tr key={op.id}>
                    <td style={{ whiteSpace: 'nowrap', color: '#5a7a8a', fontSize: 12 }}>{String(op.fecha_entrada).slice(0, 16).replace('T', ' ')}</td>
                    <td><strong>{op.plataforma}</strong><div style={{ fontSize: 10, color: '#8aa0ad' }}>{op.cuenta || 'Sin cuenta'}</div></td>
                    <td><strong>{op.instrumento}</strong><div style={{ fontSize: 10, color: '#8aa0ad' }}>{op.mercado || '—'} · {op.estrategia || 'Sin estrategia'}</div></td>
                    <td><span style={{ color: ['CORTO','SHORT','VENTA'].includes(op.direccion) ? '#dc2626' : '#059669', fontWeight: 900 }}>{op.direccion === 'LONG' || op.direccion === 'COMPRA' ? 'LARGO' : op.direccion === 'SHORT' || op.direccion === 'VENTA' ? 'CORTO' : (op.direccion || '—')}</span></td>
                    <td style={{ textAlign: 'right' }}>{Number(op.cantidad || 0).toLocaleString('es-CO')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmt(op.capital)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 900, color: Number(op.resultado) >= 0 ? '#059669' : '#dc2626' }}>{fmt(op.resultado)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 900, color: Number(op.rendimiento_pct) >= 0 ? '#059669' : '#dc2626' }}>{pct(op.rendimiento_pct)}</td>
                    <td><span style={{ fontSize: 10, fontWeight: 900, color: op.estado === 'ABIERTA' ? '#b45309' : '#0284c7', background: op.estado === 'ABIERTA' ? 'rgba(251,191,36,0.12)' : 'rgba(14,165,233,0.1)', padding: '3px 7px' }}>{op.estado}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button onClick={() => editar(op)} style={{ fontSize: 11, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button>
                      <button onClick={() => eliminar(op.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button>
                    </td>
                  </tr>
                ))}
                {!operaciones.length && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 36, color: '#8aa0ad' }}>Sin operaciones en el filtro actual</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
          <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#5a7a8a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>— Por plataforma</div>
            {(resumen?.porPlataforma || []).map(p => (
              <div key={`${p.plataforma}-${p.cuenta}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '9px 0', borderBottom: '1px solid #edf3f7' }}>
                <span style={{ color: '#08293f', fontWeight: 800 }}>{p.plataforma}{p.cuenta ? ` · ${p.cuenta}` : ''}<br /><small style={{ color: '#8aa0ad' }}>{p.operaciones} operaciones</small></span>
                <b style={{ color: Number(p.resultado) >= 0 ? '#059669' : '#dc2626' }}>{fmt(p.resultado)}</b>
              </div>
            ))}
            {!(resumen?.porPlataforma || []).length && <div style={{ color: '#8aa0ad', fontSize: 12 }}>Sin datos</div>}
          </div>

          <div style={{ background: '#08293f', border: '1px solid rgba(0,184,227,0.25)', borderRadius: 2, padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(0,184,227,0.72)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>— Últimas bitácoras</div>
            {(resumen?.ultimas || []).map(op => (
              <div key={op.id} style={{ padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <b style={{ color: '#fff', fontSize: 12 }}>{op.instrumento}</b>
                  <span style={{ color: Number(op.resultado) >= 0 ? '#34d399' : '#f87171', fontWeight: 900, fontSize: 12 }}>{fmt(op.resultado)}</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 3, lineHeight: 1.4 }}>{op.bitacora || op.estrategia || 'Sin bitácora'}</div>
              </div>
            ))}
            {!(resumen?.ultimas || []).length && <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>Sin operaciones todavía</div>}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 780 }}>
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 800, color: '#08293f', margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{editId ? 'Editar operación' : 'Nueva operación'}</h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={limpiar} style={{ fontSize: 22, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>

              <form onSubmit={guardar}>
                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div><label className="label">Entrada *</label><input className="input" type="datetime-local" value={form.fecha_entrada} onChange={e => setForm(f => ({ ...f, fecha_entrada: e.target.value }))} style={inputStyle} required /></div>
                  <div><label className="label">Salida</label><input className="input" type="datetime-local" value={form.fecha_salida} onChange={e => setForm(f => ({ ...f, fecha_salida: e.target.value }))} style={inputStyle} /></div>
                  <div><label className="label">Estado</label><select className="input" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} style={inputStyle}><option>CERRADA</option><option>ABIERTA</option></select></div>
                </div>
                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div><label className="label">Plataforma *</label><input className="input" list="trading-plataformas" value={form.plataforma} onChange={e => setForm(f => ({ ...f, plataforma: e.target.value }))} style={inputStyle} required /></div>
                  <div><label className="label">Cuenta</label><input className="input" list="trading-cuentas" value={form.cuenta} placeholder="Ej: Exness real 1" onChange={e => setForm(f => ({ ...f, cuenta: e.target.value }))} style={inputStyle} /></div>
                  <div><label className="label">Mercado</label><select className="input" value={form.mercado} onChange={e => setForm(f => ({ ...f, mercado: e.target.value }))} style={inputStyle}><option>Acciones</option><option>ETF</option><option>Futuros</option><option>Forex</option><option>Cripto</option><option>Índices</option><option>Otro</option></select></div>
                </div>
                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div><label className="label">Instrumento *</label><input className="input" list="trading-instrumentos" value={form.instrumento} placeholder="Ej: SPY, NQ, AAPL..." onChange={e => setForm(f => ({ ...f, instrumento: e.target.value.toUpperCase() }))} style={inputStyle} required /></div>
                  <div><label className="label">Dirección</label><select className="input" value={form.direccion} onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))} style={inputStyle}><option value="LARGO">Largo</option><option value="CORTO">Corto</option></select></div>
                  <div><label className="label">Cantidad</label><input className="input" type="number" step="0.000001" value={form.cantidad} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} style={inputStyle} /></div>
                </div>
                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div><label className="label">Saldo final de la cuenta *</label><input className="input" type="number" step="0.01" value={form.saldo_final} onChange={e => setForm(f => ({ ...f, saldo_final: e.target.value }))} placeholder="Ej: 317.07" required={!editId} style={inputStyle} /></div>
                  <div><label className="label">Ganancia / pérdida</label><input className="input" type="number" value={form.resultado} onChange={e => setForm(f => ({ ...f, resultado: e.target.value }))} style={{ ...inputStyle, color: resultado >= 0 ? '#059669' : '#dc2626', fontWeight: 800 }} /></div>
                  <div><label className="label">Capital operado (calculado)</label><input className="input" value={Number.isFinite(parseFloat(form.saldo_final)) ? (parseFloat(form.saldo_final) - resultado).toFixed(2) : '—'} readOnly style={{ background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f', fontWeight: 800 }} /><div style={{ fontSize: 10, color: '#6b8295', marginTop: 3 }}>Saldo final − resultado</div></div>
                </div>
                <div style={{ marginBottom: 12 }}><label className="label">Estrategia / Setup</label><input className="input" value={form.estrategia} onChange={e => setForm(f => ({ ...f, estrategia: e.target.value }))} placeholder="Ej: ruptura, pullback, soporte..." style={inputStyle} /></div>
                <div style={{ marginBottom: 18 }}><label className="label">Bitácora</label><textarea className="input" rows={4} value={form.bitacora} onChange={e => setForm(f => ({ ...f, bitacora: e.target.value }))} placeholder="Aspectos que tuve en cuenta para la entrada, manejo emocional, salida..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} /></div>
                <datalist id="trading-plataformas">{['Exness', ...listas.plataformas].filter((v, i, a) => v && a.indexOf(v) === i).map(v => <option key={v} value={v} />)}</datalist>
                <datalist id="trading-cuentas">{listas.cuentas.map(v => <option key={v} value={v} />)}</datalist>
                <datalist id="trading-instrumentos">{listas.instrumentos.map(v => <option key={v} value={v} />)}</datalist>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn-primary" disabled={loading} type="submit" style={{ flex: 1 }}>{loading ? 'Guardando...' : editId ? 'Actualizar' : 'Guardar operación'}</button>
                  <button className="btn-secondary" type="button" onClick={limpiar}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 980px) {
          .trading-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
